import { datadogLogs, type LogsInitConfiguration } from '@datadog/browser-logs'
import config from '../config'
import { isAutomatedTrafficUA } from './automatedTraffic'
import {
  redactTokenUrls,
  shouldSuppressCapabilityTelemetry
} from './capabilityPrivacy'
import { isKnownClientErrorNoise, isStaleChunkError } from './clientErrorNoise'
import { handleStaleChunkError } from './staleChunkReload'

const logsConfig: LogsInitConfiguration = {
  clientToken: config.datadog.clientToken,
  site: 'datadoghq.com',
  forwardErrorsToLogs: true,
  sessionSampleRate: 100,
  env: config.env,
  service: 'spotme-client',
  version: config.version,
  beforeSend: (event) => {
    if (shouldSuppressCapabilityTelemetry(event)) return false
    // Redacted token pages keep their error logs, minus the token (view.url,
    // view.referrer, message, stack). Datadog's contract is to MUTATE the
    // event, so copy the redacted top-level fields back onto it.
    const redacted = redactTokenUrls(event)
    if (redacted !== event) Object.assign(event, redacted)
    // Local dev still runs this module-level init, but must NEVER ship browser
    // logs/errors to the shared Datadog app. Otherwise dev network drops (e.g.
    // "Failed to fetch" → http://127.0.0.1:3100/graphql when the local backend
    // isn't running) leak into the prod `spotme-client` service tagged env:local
    // and pollute dashboards. The default export already routes local logging to
    // `console`; this closes the forwardErrorsToLogs side channel too.
    if (config.env === 'local') {
      return false
    }

    const haystack = [event.message, event.error?.message, event.error?.stack]
      .filter((s): s is string => typeof s === 'string')
      .join(' ')

    // React hydration errors (#418/#423/#425) from automated traffic. Strict
    // bot renderers (Bingbot, HeadlessChrome, Lighthouse) trip React 19's
    // attribute/whitespace serialization compare on SSR'd listing pages; real
    // users don't at meaningful rates. captureHydrationDiff (_app.tsx) already
    // skips these UAs for our custom recorder — but forwardErrorsToLogs catches
    // React's raw console.error independently, so it needs the same skip to
    // stay consistent. Real-user #418 stays visible; only bot UAs are dropped.
    if (
      /Minified React error #(418|423|425)/.test(haystack) &&
      typeof navigator !== 'undefined' &&
      isAutomatedTrafficUA(navigator.userAgent)
    ) {
      return false
    }

    // Drop benign MUI Autocomplete race: inputRef.current.focus() fires after
    // unmount. Caught by React's reportGlobalError — no UX impact, just noise.
    // beforeSend runs in-browser on the MINIFIED stack, so match on the error
    // message (property names like 'focus' are preserved in TypeError text).
    // V8:    Cannot read properties of null (reading 'focus')
    // Safari: null is not an object (evaluating 'eo.current.focus')
    const mentionsFocus = haystack.includes("'focus'")
    const mentionsNullish =
      haystack.includes('null') || haystack.includes('undefined')
    if (mentionsFocus && mentionsNullish) {
      return false
    }

    // Stale-deploy chunk failure: reload once (per deploy + path) to pick up
    // the new bundle — see `staleChunkReload.ts`. Drop while the reload is in
    // flight; log if it already happened and the chunk still fails. Those
    // post-reload survivors DO reach Datadog Error Tracking (nothing drops
    // "Loading chunk " at ingest) — in practice one flaky mobile connection
    // every few days, not a SpotMe fault.
    if (isStaleChunkError(haystack)) {
      return handleStaleChunkError() === 'exhausted'
    }

    // Third-party pixel / analytics / ad-tech scripts routinely fail to send
    // when blocked by adblockers, tracking-protection, or a CDN hiccup — a
    // fetch failure to any of these is always noise, never a SpotMe fault.
    // These were the top volume in Datadog (Vercel Analytics 1.1k/day, Meta
    // Pixel 786/day, etc — 2026-07). Match on the resource URL the SDK attaches
    // as `event.http?.url`, falling back to the stack haystack.
    const pixelUrl = typeof event.http?.url === 'string' ? event.http.url : ''
    const pixelHostnames = [
      'px.ads.linkedin.com',
      '_vercel/speed-insights',
      '_vercel/insights',
      'google.com/ccm/form-data',
      'google.com/ccm/collect',
      'google.com/rmkt/',
      'analytics.google.com/g/collect',
      'ad.doubleclick.net',
      'facebook.com/tr',
      'bat.bing.com',
      'analytics.ahrefs.com',
      'capig.datah04.com',
      'analytics.tiktok.com'
    ]
    if (
      pixelHostnames.some((h) => pixelUrl.includes(h) || haystack.includes(h))
    ) {
      return false
    }

    // Everything from here that depends only on the error TEXT lives in
    // `clientErrorNoise.ts`, because PostHog's `capture_exceptions` needs the
    // exact same verdicts and the two sinks silently drifted apart until
    // 2026-09-06 (an unfiltered `UET is not defined` in PostHog was read as a
    // broken Bing pixel on our busiest paid lander; Datadog had been dropping
    // it correctly since 2026-08-30). Covered there, with the full rationale
    // for each: aborted fetches, blocked Bing UET, spoofed-UA Symbol errors,
    // the three Google Maps families, cross-origin "Script error.",
    // in-app-WebView injected scripts (Android globals and the iOS bridge), and
    // the WebKit IndexedDB teardown family — that last one was filtered right
    // here from 2026-08-11 and moved there 2026-09-12, because PostHog had
    // never inherited it and was showing all three of its messages as live
    // faults (one auto-scored `severity: high`).
    //
    // `pixelUrl` is folded into the haystack so the Maps `mapConfigs:batchGet`
    // arm still matches on the resource URL of a failed fetch, which is where
    // that failure surfaces rather than in the message.
    if (
      isKnownClientErrorNoise({
        haystack: [haystack, pixelUrl].filter(Boolean).join(' '),
        messages: [event.message, event.error?.message],
        error: event.error
      })
    ) {
      return false
    }

    // Client-side network fetch that never reached the server — status_code 0,
    // or "Failed to fetch" (Chrome) / "Load failed" (Safari) / "NetworkError"
    // (Firefox), on a network-origin event. These are aborted or dropped
    // requests: the user navigated away mid-request, lost signal on mobile
    // cellular, or a bot bailed — NOT backend faults. A real server error
    // carries a 4xx/5xx AND surfaces in the spotme-server logs. Confirmed
    // 2026-07: 305/305 GraphQL fetch errors were status 0 with ZERO backend
    // correlation (service:spotme-server = 1 error/24h) — ~310/day of pure
    // noise. Scoped to our own GraphQL endpoint (matches prod
    // server.spotme.mx/graphql AND dev.server.spotme.mx/graphql) so a genuine
    // failed request to some other host still logs.
    const isNetworkDrop =
      event.origin === 'network' &&
      (event.http?.status_code === 0 ||
        /Failed to fetch|Load failed|NetworkError when attempting to fetch/i.test(
          haystack
        ))
    // Third-party telemetry belongs here too: PostHog (`/flags`, `/array/…/config`,
    // capture) and the GA4 `www.google.com/g/collect` beacon — note the existing
    // pixelHostnames entry only covers the `analytics.google.com` host, so the
    // `www` variant fell through. These fail whenever a user is on flaky mobile
    // data, runs an ad blocker, or navigates mid-beacon; PostHog queues and
    // retries on its own and the SDK degrades to default flags.
    // Deliberately scoped to the network-drop branch rather than the blanket
    // pixelHostnames list: a real 4xx/5xx from these hosts (a broken PostHog
    // project config, say) is a genuine signal and still logs.
    const networkDropHosts = [
      'server.spotme.mx/graphql',
      'i.posthog.com',
      'google.com/g/collect'
    ]
    if (
      isNetworkDrop &&
      networkDropHosts.some((h) => pixelUrl.includes(h) || haystack.includes(h))
    ) {
      return false
    }

    // PostHog client-side capture rate-limiting + Google Identity FedCM network
    // rejections — third-party SDK internals with no SpotMe action. ~590/day
    // combined, all benign (PostHog self-throttles; FedCM token retrieval drops
    // on flaky mobile networks and the sign-in still works via the fallback).
    if (
      haystack.includes(
        'This capture call is ignored due to client rate limiting'
      ) ||
      /\[GSI_LOGGER\][^]*FedCM[^]*(NetworkError|Error retrieving a token)/i.test(
        haystack
      )
    ) {
      return false
    }

    return true
  }
}

if (!shouldSuppressCapabilityTelemetry()) datadogLogs.init(logsConfig)

export default config.env === 'local' ? console : datadogLogs.logger
